import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { addDays, weekdayOf } from '@kraftwerk/shared';
import type { IsoDate } from '@kraftwerk/shared';
import { inPlanScope } from '../../rooms.js';
import type { PlanScope } from '../../rooms.js';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api.js';
import { currentOwnerId } from '../../auth.js';
import { db } from '../../db/local.js';
import type { LocalHouseholdMember } from '../../db/local.js';
import { listMembers } from '../../db/shopping.js';
import { listAgenda } from '../../db/calendar.js';
import type { AgendaItem } from '../../db/calendar.js';
import { currentHouseholdId } from '../../db/shopping.js';
import { formatDay } from '../../format.js';
import { useTimeZone, useToday } from '../../hooks/useToday.js';
import { useMayPlan } from '../../hooks/useModules.js';
import { FEEDS_KEY } from '../CalendarSetting.js';
import { EventEditor } from './EventEditor.js';
import { FeedEventSheet } from './FeedEventSheet.js';
import type { NoteEventPreset } from '../journal/DayNotes.js';
import { notedEventIds } from '../../db/notes.js';
import { useSwipePager } from '../../gestures/SwipeProvider.js';

type Mode = 'agenda' | 'week' | 'month';
const MODES: Mode[] = ['agenda', 'week', 'month'];

/** Montag der Woche eines Tages. */
function mondayOf(date: string): string {
  return addDays(date as IsoDate, 1 - weekdayOf(date));
}

function firstOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

function addMonths(date: string, months: number): string {
  const [y, m] = date.split('-').map(Number);
  const total = y! * 12 + (m! - 1) + months;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}-01`;
}

/**
 * Der Kalender: Agenda (Liste), Woche, Monat. Ein Eintrag öffnet sich zum
 * Bearbeiten (eigene Termine), zum Markieren (eingelesene) oder verweist ins
 * Training (Trainingstermine — die gehören dorthin).
 */
export function CalendarView({
  onOpenTraining,
  onWriteNote,
  onOpenTask,
  onRefreshed,
  onConnectCalendar,
  scope = 'all',
}: {
  /** Leer? Dann den Weg zeigen, Apple/Google einzulesen. */
  onConnectCalendar?: (() => void) | undefined;
  onOpenTraining: () => void;
  onWriteNote: (preset: NoteEventPreset) => void;
  /** Aufgabe im Kalender angetippt: zur Liste wechseln. */
  onOpenTask: (id: string) => void;
  /** Nach dem Nachholen der Abos: Abgleich anstoßen, damit die Termine ankommen. */
  onRefreshed: () => void;
  /** Räume (27.10.2026): meins, unseres oder alles — `inPlanScope`. */
  scope?: PlanScope;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const today = useToday();
  // Kleine Kinder (E20) sehen Termine, legen aber keine an.
  const mayPlan = useMayPlan();
  const [mode, setMode] = useState<Mode>('agenda');
  const [anchor, setAnchor] = useState<string | null>(null);
  const [agendaDays, setAgendaDays] = useState(30);
  const [pastDays, setPastDays] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<AgendaItem | 'new' | null>(null);
  const [newDate, setNewDate] = useState<string | null>(null);
  const [feedItem, setFeedItem] = useState<AgendaItem | null>(null);

  const householdId = useLiveQuery(() => currentHouseholdId(db), [], null);
  const noted = useLiveQuery(() => notedEventIds(db), [], new Set<string>());
  // Für wen (E16): Namen statt Kennungen an der Terminzeile.
  const members = useLiveQuery(
    () => listMembers(householdId ?? null, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  const memberName = new Map(members.map((member) => [member.userId, member.displayName]));
  const feeds = useQuery({
    queryKey: FEEDS_KEY,
    queryFn: async () =>
      (await apiFetch<{ feeds: { id: string; name: string }[] }>('/calendar/feeds')).feeds,
    retry: false,
  });
  const feedName = (id: string | null) =>
    feeds.data?.find((feed) => feed.id === id)?.name ?? t('calendar.imported');

  // Abos nachholen, sobald der Kalender aufgeht — der Server holt höchstens
  // alle 15 Minuten; scheitert es (offline), bleibt der letzte Stand.
  useEffect(() => {
    void apiFetch<{ refreshed: number }>('/calendar/refresh', { method: 'POST' })
      .then((result) => {
        if (result.refreshed > 0) onRefreshed();
      })
      .catch(() => undefined);
  }, [onRefreshed]);

  const base = anchor ?? today;
  const range = useMemo(() => {
    if (!base) return null;
    if (mode === 'week') {
      const from = mondayOf(base);
      return { from, to: addDays(from as IsoDate, 6) };
    }
    if (mode === 'month') {
      const first = firstOfMonth(base);
      const from = mondayOf(first);
      const last = addDays(addMonths(first, 1) as IsoDate, -1);
      return { from, to: addDays(mondayOf(last) as IsoDate, 6) };
    }
    return {
      from: addDays(base as IsoDate, -pastDays),
      to: addDays(base as IsoDate, agendaDays - 1),
    };
  }, [base, mode, agendaDays, pastDays]);

  // Wischen auf Woche oder Monat blättert (24.10.2026) — wie die Pfeile.
  const pager = useSwipePager<HTMLElement>(
    () =>
      base &&
      setAnchor(mode === 'week' ? addDays(base as IsoDate, -7) : addMonths(firstOfMonth(base), -1)),
    () =>
      base &&
      setAnchor(mode === 'week' ? addDays(base as IsoDate, 7) : addMonths(firstOfMonth(base), 1)),
  );
  const me = currentOwnerId();
  const trainingTitle = t('calendar.training');
  const allItems = useLiveQuery(
    () =>
      range && timeZone
        ? listAgenda(range.from, range.to, { timeZone, me, trainingTitle }, db)
        : Promise.resolve([] as AgendaItem[]),
    [range?.from, range?.to, timeZone, me, trainingTitle],
    [] as AgendaItem[],
  );
  const items = allItems.filter((item) => inPlanScope(item, scope, me));

  if (!today || !range) return <></>;

  const open = (item: AgendaItem) => {
    if (item.kind === 'training') onOpenTraining();
    else if (item.kind === 'task') onOpenTask(item.id);
    else if (item.kind === 'feed') setFeedItem(item);
    else if (mayPlan) setEditing(item);
  };

  if (editing) {
    return (
      <EventEditor
        item={editing === 'new' ? null : editing}
        defaultDate={newDate ?? selected ?? today}
        householdId={householdId}
        onWriteNote={onWriteNote}
        onClose={() => {
          setEditing(null);
          setNewDate(null);
        }}
      />
    );
  }

  const byDay = new Map<string, AgendaItem[]>();
  for (const item of items) {
    // Mehrtägige stehen an jedem ihrer Tage im Fenster.
    const last = item.endDate ?? item.date;
    for (
      let day = item.date < range.from ? range.from : item.date;
      day <= last && day <= range.to;
      day = addDays(day as IsoDate, 1)
    ) {
      const list = byDay.get(day) ?? [];
      list.push(item);
      byDay.set(day, list);
    }
  }

  return (
    <section aria-labelledby="calendar-heading" className="flex flex-col gap-3">
      <h2 id="calendar-heading" className="sr-only">
        {t('calendar.heading')}
      </h2>
      <div className="flex items-center justify-between gap-2">
        <div role="group" aria-label={t('calendar.view')} className="flex gap-1">
          {MODES.map((entry) => (
            <button
              key={entry}
              type="button"
              aria-pressed={mode === entry}
              onClick={() => {
                setMode(entry);
                setAnchor(null);
                setSelected(null);
              }}
              className="kw-toggle"
            >
              {t(`calendar.mode.${entry}`)}
            </button>
          ))}
        </div>
        {mayPlan ? (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="kw-btn kw-btn-small shrink-0 px-3"
          >
            {t('calendar.new')}
          </button>
        ) : null}
      </div>

      {mode !== 'agenda' ? (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() =>
              setAnchor(
                mode === 'week'
                  ? addDays(base! as IsoDate, -7)
                  : addMonths(firstOfMonth(base!), -1),
              )
            }
            aria-label={t(`calendar.previous.${mode}`)}
            className="kw-step"
          >
            ←
          </button>
          <p className="text-center text-sm font-semibold">
            {mode === 'week'
              ? `${formatDay(range.from, 'date', i18n.language)} – ${formatDay(range.to, 'date', i18n.language)}`
              : new Date(`${firstOfMonth(base!)}T12:00:00`).toLocaleDateString(i18n.language, {
                  month: 'long',
                  year: 'numeric',
                })}
          </p>
          <button
            type="button"
            onClick={() =>
              setAnchor(
                mode === 'week' ? addDays(base! as IsoDate, 7) : addMonths(firstOfMonth(base!), 1),
              )
            }
            aria-label={t(`calendar.next.${mode}`)}
            className="kw-step"
          >
            →
          </button>
        </div>
      ) : null}

      {/* Nur das Raster gleitet — Pfeile und Zeitraum oben bleiben stehen. */}
      <div ref={mode === 'agenda' ? undefined : pager} className="flex flex-col gap-3">
        {mode === 'month' ? (
          <MonthGrid
            from={range.from}
            to={range.to}
            month={firstOfMonth(base!).slice(0, 7)}
            today={today}
            byDay={byDay}
            selected={selected}
            onSelect={setSelected}
          />
        ) : null}

        {mode === 'agenda' && pastDays === 0 ? (
          <button
            type="button"
            onClick={() => setPastDays(30)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('calendar.earlier')}
          </button>
        ) : null}

        <DayList
          days={
            mode === 'month'
              ? // Ohne Auswahl der heutige Tag, wenn er im Monat liegt — ein leerer
                // Platz unter dem Raster sähe aus wie „nichts los".
                selected
                ? [selected]
                : today.startsWith(firstOfMonth(base!).slice(0, 7))
                  ? [today]
                  : []
              : mode === 'week'
                ? Array.from({ length: 7 }, (_, index) => addDays(range.from as IsoDate, index))
                : [...byDay.keys()].sort()
          }
          byDay={byDay}
          today={today}
          showEmpty={mode !== 'agenda'}
          feedName={feedName}
          noted={noted}
          memberName={memberName}
          onOpen={open}
          onAdd={
            mayPlan
              ? (day) => {
                  setNewDate(day);
                  setEditing('new');
                }
              : undefined
          }
        />
      </div>

      {mode === 'agenda' && byDay.size === 0 ? (
        <div className="kw-tile flex flex-col gap-2">
          <p className="text-sm text-ink-muted">{t('calendar.empty')}</p>
          {onConnectCalendar ? (
            <button
              type="button"
              onClick={onConnectCalendar}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('calendar.connectHint')}
            </button>
          ) : null}
        </div>
      ) : null}
      {mode === 'agenda' ? (
        <button
          type="button"
          onClick={() => setAgendaDays(agendaDays + 30)}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('calendar.later')}
        </button>
      ) : null}

      {feedItem ? (
        <FeedEventSheet
          item={feedItem}
          source={feedName(feedItem.feedId)}
          onWriteNote={onWriteNote}
          onClose={() => setFeedItem(null)}
        />
      ) : null}
    </section>
  );
}

function DayList({
  days,
  byDay,
  today,
  showEmpty,
  feedName,
  noted,
  memberName,
  onOpen,
  onAdd,
}: {
  days: string[];
  byDay: Map<string, AgendaItem[]>;
  today: string;
  showEmpty: boolean;
  feedName: (id: string | null) => string;
  noted: Set<string>;
  /** Für wen (E16): Kennung → Name. */
  memberName: Map<string, string>;
  onOpen: (item: AgendaItem) => void;
  /** Fehlt bei kleinen Kindern (E20) — dann kein „+“ am Tag. */
  onAdd?: ((day: string) => void) | undefined;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <ol className="flex flex-col gap-2">
      {days.map((day) => {
        const list = byDay.get(day) ?? [];
        if (list.length === 0 && !showEmpty) return null;
        return (
          <li key={day} className="kw-tile flex flex-col gap-1 p-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className={`text-sm font-semibold ${day === today ? 'text-accent' : ''}`}>
                {formatDay(day, 'long', i18n.language)}
                {day === today ? ` · ${t('calendar.today')}` : ''}
              </h3>
              {onAdd ? (
                <button
                  type="button"
                  onClick={() => onAdd(day)}
                  aria-label={t('calendar.addOn', { date: formatDay(day, 'long', i18n.language) })}
                  className="kw-btn kw-btn-quiet kw-btn-small px-3"
                >
                  +
                </button>
              ) : null}
            </div>
            {list.length === 0 ? (
              <p className="text-xs text-ink-muted">{t('calendar.free')}</p>
            ) : (
              <ul className="flex flex-col">
                {list.map((item) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => onOpen(item)}
                      className="flex min-h-11 w-full items-center gap-3 text-left"
                    >
                      <span className="kw-figure w-12 shrink-0 text-sm">
                        {item.time ?? t('calendar.allDay')}
                      </span>
                      <span
                        className={`min-w-0 flex-1 ${item.cancelled ? 'text-ink-muted line-through' : ''}`}
                      >
                        <span className="block truncate text-sm">{item.title}</span>
                        <span className="block truncate text-xs text-ink-muted">
                          {item.kind === 'household'
                            ? t('calendar.kind.household')
                            : item.kind === 'feed'
                              ? feedName(item.feedId)
                              : item.kind === 'training'
                                ? t('calendar.kind.training')
                                : item.kind === 'task'
                                  ? t('calendar.kind.task', { list: item.note ?? '' })
                                  : t('calendar.kind.event')}
                          {item.activity ? ` · ${t(`activity.${item.activity}`)}` : ''}
                          {item.location ? ` · ${item.location}` : ''}
                          {item.forUserIds.length > 0
                            ? ` · ${t('calendar.forWhom', {
                                names: item.forUserIds
                                  .map((id) => memberName.get(id))
                                  .filter((name): name is string => !!name)
                                  .join(', '),
                              })}`
                            : ''}
                          {noted.has(item.id) ? ` · ${t('notes.hasNote')}` : ''}
                        </span>
                      </span>
                      {item.cancelled ? (
                        <span className="kw-chip shrink-0 border-warn text-warn">
                          {t('calendar.cancelled')}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function MonthGrid({
  from,
  to,
  month,
  today,
  byDay,
  selected,
  onSelect,
}: {
  from: string;
  to: string;
  month: string;
  today: string;
  byDay: Map<string, AgendaItem[]>;
  selected: string | null;
  onSelect: (day: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const days: string[] = [];
  for (let day = from; day <= to; day = addDays(day as IsoDate, 1)) days.push(day);
  const weekdays = days
    .slice(0, 7)
    .map((day) =>
      new Date(`${day}T12:00:00`).toLocaleDateString(i18n.language, { weekday: 'narrow' }),
    );
  return (
    <div className="kw-tile p-2">
      <div aria-hidden="true" className="grid grid-cols-7 text-center text-xs text-ink-muted">
        {weekdays.map((name, index) => (
          <span key={index}>{name}</span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-0.5">
        {days.map((day) => {
          const count = (byDay.get(day) ?? []).filter((item) => !item.cancelled).length;
          const inMonth = day.startsWith(month);
          return (
            <button
              key={day}
              type="button"
              aria-pressed={selected === day}
              aria-label={`${formatDay(day, 'long', i18n.language)}: ${t('calendar.entries', { count })}`}
              onClick={() => onSelect(day)}
              className={`flex h-11 flex-col items-center justify-center rounded-lg text-sm ${
                selected === day
                  ? 'bg-accent text-surface'
                  : day === today
                    ? 'border border-accent'
                    : ''
              } ${inMonth ? '' : 'text-ink-muted opacity-60'}`}
            >
              {Number(day.slice(8))}
              <span aria-hidden="true" className="h-1.5 text-[0.5rem] leading-none">
                {count > 0 ? '•'.repeat(Math.min(count, 3)) : ''}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
